import { useEffect, useState } from "react";
import { IMAGE_URL } from "../../path";

interface GalleryImage {
    id: number;
    image_path: string;
}

interface GalleryData {
    title: string;
    images: GalleryImage[];
    description?: string;
    year?: string;
}

interface CustomGalleryProps {
    data: GalleryData;
}

function CustomGalleryComponent({ data }: CustomGalleryProps) {
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        isDesktop: window.innerWidth > 950,
        isTablet: window.innerWidth <= 950 && window.innerWidth > 575,
        isMobile: window.innerWidth <= 575,
    });

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setScreenSize({
                width,
                isDesktop: width > 950,
                isTablet: width <= 950 && width > 575,
                isMobile: width <= 575,
            });
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (!lightboxOpen) return;
            if (e.key === "Escape") setLightboxOpen(false);
            if (e.key === "ArrowRight")
                setPhotoIndex((i) => (i + 1) % data.images.length);
            if (e.key === "ArrowLeft")
                setPhotoIndex(
                    (i) => (i + data.images.length - 1) % data.images.length
                );
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [lightboxOpen, data.images.length]);

    const images = data.images.map((img) => IMAGE_URL + img.image_path);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center", // centers outer container content
                marginTop: "100px",
                marginBottom: "80px",
                width: "95%",
                marginLeft: "auto", // center container horizontally
                marginRight: "auto",
            }}
        >
            {/* Title & Description */}
            <div
                style={{
                    display: "flex",
                    flexDirection: screenSize.isMobile ? "column" : "row",
                    gap: "20px",
                    marginBottom: "30px",
                    width: "100%",
                }}
            >
                <div style={{ flex: 1 }}></div>
                <div
                    style={{
                        flex: 1,
                        textAlign: "left",
                        paddingRight: "20px",
                        paddingLeft: screenSize.isMobile ? "20px" : "50px",
                    }}
                >
                    <p style={{ fontWeight: 600, textTransform: "uppercase" }}>
                        {data.title}
                    </p>
                    {data.year && (
                        <p style={{ fontWeight: 600, fontSize: "10px" }}>{data.year}</p>
                    )}
                    {data.description && (
                        <p
                            style={{
                                width: screenSize.isMobile ? "95%" : "60%",
                                marginBottom: "10px",
                                fontWeight: 400,
                            }}
                        >
                            {data.description}
                        </p>
                    )}
                </div>
            </div>

            {/* Image Grid */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: screenSize.isDesktop
                        ? "repeat(8, 1fr)"
                        : screenSize.isTablet
                            ? "repeat(4, 1fr)"
                            : "repeat(2, 1fr)",
                    gap: screenSize.isMobile ? "10px" : "15px",
                    padding: screenSize.isMobile
                        ? "0 10px"
                        : screenSize.isTablet
                            ? "0 30px"
                            : "0 50px",
                    width: "100%",
                    justifyItems: "start", // ✅ aligns images to the left
                }}
            >
                {images.map((image, index) => (
                    <div
                        key={index}
                        style={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            overflow: "hidden",
                            cursor: "pointer",
                        }}
                        onClick={() => {
                            setPhotoIndex(index);
                            setLightboxOpen(true);
                        }}
                    >
                        <img
                            src={image}
                            alt={`Gallery Image ${index + 1}`}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.3s ease",
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.transform = "scale(1.05)")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                            }
                        />
                    </div>
                ))}
            </div>

            {/* Lightbox */}
            {lightboxOpen && (
                <div
                    onClick={() => setLightboxOpen(false)}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(0, 0, 0, 0.9)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                >
                    <img
                        src={images[photoIndex]}
                        alt=""
                        style={{
                            maxWidth: "90%",
                            maxHeight: "90%",
                            objectFit: "contain",
                            userSelect: "none",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    {images.length > 1 && (
                        <>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPhotoIndex(
                                        (photoIndex + images.length - 1) % images.length
                                    );
                                }}
                                style={arrowStyle("left")}
                            >
                                ‹
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPhotoIndex((photoIndex + 1) % images.length);
                                }}
                                style={arrowStyle("right")}
                            >
                                ›
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );

}

function arrowStyle(side: "left" | "right"): React.CSSProperties {
    return {
        position: "absolute",
        top: "50%",
        [side]: "30px",
        transform: "translateY(-50%)",
        background: "none",
        color: "white",
        fontSize: "3rem",
        border: "none",
        cursor: "pointer",
        userSelect: "none",
    };
}

export default CustomGalleryComponent;
